import { showAlert } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import {
  useCheckMethodsForEmail,
  useLookupUserAccount,
  useSendPasswordResetEmail,
} from '@/features/auth/queries';
import type { EnterEmailFormValues } from '@/features/auth/schema';
import { forgotPasswordSchema } from '@/features/auth/schema/enter-email-schema';
import { useUnAuthStackNavigation } from '@/hooks';
import { captureException } from '@/libs/logger';
import { useUiStore } from '@/stores';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useShallow } from 'zustand/shallow';

export function useForgotPassword() {
  const navigation = useUnAuthStackNavigation();
  const { mutateAsync: sendResetEmail } = useSendPasswordResetEmail();
  const { mutateAsync: checkMethodsForEmail } = useCheckMethodsForEmail();
  const { mutateAsync: lookupUserAccount } = useLookupUserAccount();
  const showGlobalLoading = useUiStore(useShallow(s => s.showGlobalLoading));
  const hideGlobalLoading = useUiStore(useShallow(s => s.hideGlobalLoading));

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<EnterEmailFormValues>({
    resolver: yupResolver(forgotPasswordSchema),
    mode: 'onSubmit',
    defaultValues: {
      email: '',
    },
  });

  const {
    field: { value: email, onChange: onChangeEmail, onBlur: onBlurEmail },
  } = useController({
    name: 'email',
    control,
  });

  const handleSendResetEmail = useCallback(
    async (data: EnterEmailFormValues) => {
      try {
        showGlobalLoading();
        const methods = await checkMethodsForEmail(data.email);
        if (methods.length > 0) {
          const userAccount = await lookupUserAccount(data.email);
          if (userAccount) {
            await sendResetEmail(data.email);
            navigation.navigate('ForgotPasswordLinkSent', { email: data.email });
          }
        } else {
          showAlert({
            title: STRINGS.auth.enterEmail.noticeTitle,
            message: STRINGS.auth.enterEmail.errorEmailNotRegist,
          });
        }
      } catch (error) {
        captureException(
          {
            error_payload: error,
          },
          { tags: { usk_log_type: 'handle_send_reset_email' } },
        );
      } finally {
        hideGlobalLoading();
      }
    },
    [
      showGlobalLoading,
      checkMethodsForEmail,
      lookupUserAccount,
      sendResetEmail,
      navigation,
      hideGlobalLoading,
    ],
  );

  const handleFormSubmit = handleSubmit(handleSendResetEmail);

  const handleCancel = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  return {
    email,
    onChangeEmail,
    onBlurEmail,
    errors,
    isSubmitting,
    handleSendResetEmail: handleFormSubmit,
    handleCancel,
  };
}
